/**
 * @author NikolaV
 */

import * as React from 'react';
import { observer } from 'mobx-react';

import { DateRangePicker } from '@shared/ui-react/DateRangePicker';
import { LogpointsFilterStore } from '@shared/logpoints/LogpointsFilterStore';
import { CheckboxListDropdown } from '@shared/ui-react/CheckboxListDropdown';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from '@shared/ui-react/Button';
import { Telemetry, TelemetryEventType, TelemetrySource } from '@shared/Telemetry';

@observer
export class LogpointsFilter extends React.Component<{
    store: LogpointsFilterStore;
    mobile?: boolean;
}> {
    render() {
        const store = this.props.store;
        const filter = this.props.store.filter;

        return (
            <div className='app__common-filters__wrapper' style={{ maxWidth: 460 }}>
                <main>
                    <DateRangePicker
                        store={filter}
                        onChange={(a, b, c) => {
                            store.setDates(a, b, c);
                            Telemetry.add(TelemetryEventType.MAP_ASBUILT_FILTER_PERIOD, TelemetrySource.MAP_2D_DESKTOP);
                        }}
                        visiblePresetsCount={5}
                        mobile={this.props.mobile}
                        clearable={true}
                    />

                    <div className='filters-row'>
                        <div className='flex-column'>
                            <span>{__('js_logpoints.column_code')}</span>
                            <CheckboxListDropdown
                                list={store.getSortedCodes()}
                                onChange={this.handleCodesChange}
                                selected={store.filter.codes ?? []}
                                sort={false}
                                itemTemplate={item => (
                                    <span>
                                        {item.title} {item.data?.count ? <span style={{ fontSize: '0.9em', opacity: 0.8 }}>({item.data.count})</span> : null}
                                    </span>
                                )}
                            />
                        </div>
                    </div>
                    <div className='filters-row'>
                        <div className='flex-column'>
                            <span>{__('general.user')}</span>
                            <CheckboxListDropdown list={store.creators} onChange={this.handleCreatorChange} selected={store.filter.userIds ?? []} />
                        </div>
                    </div>

                    <div className='filters-row'>
                        <div className='flex-column'>
                            <span>{__('general.vehicle')}</span>
                            <CheckboxListDropdown list={store.vehicles} onChange={this.handleVehicleChange} selected={store.filter.vehicleIds ?? []} />
                        </div>

                        <div className='flex-column'>
                            <span>{__('js_logpoints.column_source')}</span>
                            <CheckboxListDropdown list={store.sources} onChange={this.handleSourcesChange} selected={store.filter.sources ?? []} />
                        </div>
                    </div>

                    <div className='filters-row'>
                        <div className='flex-column'>
                            <span>{__('js_logpoints.column_surface_code')}</span>
                            <CheckboxListDropdown
                                list={store.surfaceCodeOptions}
                                onChange={this.handleSurfaceCodesChange}
                                selected={store.filter.surfaceCodes ?? []}
                            />
                        </div>

                        <div className='flex-column'>
                            <span>{__('js_logpoints.column_point_name')}</span>
                            <CheckboxListDropdown
                                list={store.pointNameOptions}
                                onChange={this.handlePointNamesChange}
                                selected={store.filter.pointNames ?? []}
                            />
                        </div>
                    </div>

                    <div className='filters-row'>
                        <div className='flex-column' style={{ flex: 0 }}>
                            <span style={{ whiteSpace: 'nowrap' }}>
                                {__(filter.hideDzMode === 'inside' ? 'js_logpoints.dz_filter_inside_range' : 'js_logpoints.dz_filter_outside_range')} ±
                            </span>
                            <div className='flex-row'>
                                <InputNumber
                                    min={0}
                                    step={0.1}
                                    maxFractionDigits={2}
                                    minFractionDigits={2}
                                    useGrouping={false}
                                    allowEmpty={false}
                                    style={{ width: 70 }}
                                    value={filter.hideDzPlusMinus}
                                    onChange={e => (filter.hideDzPlusMinus = e.value)}
                                />

                                <Button
                                    subtle
                                    secondary
                                    icon='iki iki-sync-status'
                                    iconSize={14}
                                    onClick={() => (filter.hideDzMode = filter.hideDzMode === 'inside' ? 'outside' : 'inside')}
                                    tooltip={__('js_logpoints.dz_filter_switch_mode')}
                                />
                            </div>
                        </div>

                        <div className='flex-column'>
                            <span>{__('js_logpoints.column_stage')}</span>
                            <CheckboxListDropdown list={store.approvals} onChange={this.handleApprovalsChange} selected={store.filter.approvals ?? []} sort={false} />
                        </div>
                    </div>

                    <div>
                        <label className='checkbox-label' style={{ margin: '7px 0', display: 'inline-flex' }}>
                            <input type='checkbox' className='form-control' checked={filter.onlyOutOfTolerance} onChange={this.handleToleranceChange} />
                            <span>{__('js_map.logpoints.show_only_out_of_tolerance')}</span>
                        </label>
                    </div>

                    <div style={{ marginTop: '7px 0' }}>
                        <span>{__('js_logpoints.filter_mode')}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 12 }}>
                        <label className='checkbox-label' style={{ display: 'inline-flex' }}>
                            <input
                                type='radio'
                                className='form-control'
                                checked={filter.filterMode !== 'or'}
                                onChange={() => (filter.filterMode = 'and')}
                            />
                            <span>{__('js_logpoints.filter_all')}</span>
                        </label>
                        <label className='checkbox-label' style={{ display: 'inline-flex' }}>
                            <input
                                type='radio'
                                className='form-control'
                                checked={filter.filterMode === 'or'}
                                onChange={() => (filter.filterMode = 'or')}
                            />
                            <span>{__('js_logpoints.filter_any')}</span>
                        </label>
                    </div>
                </main>
            </div>
        );
    }

    handleCreatorChange = (activeIds: string[]) => {
        this.props.store.filter.userIds = activeIds.map(e => parseInt(e));
    };

    handleVehicleChange = (activeIds: string[]) => {
        this.props.store.filter.vehicleIds = activeIds.map(e => parseInt(e));
    };

    handleCodesChange = (codes: string[]) => {
        this.props.store.filter.codes = codes;
    };

    handleSourcesChange = (sources: string[]) => {
        this.props.store.filter.sources = sources as LogpointSource[];
    };

    handleApprovalsChange = (approvals: string[]) => {
        this.props.store.filter.approvals = approvals as LogpointApprovalStage[];
    };

    handleSurfaceCodesChange = (surfaceCodes: string[]) => {
        this.props.store.filter.surfaceCodes = surfaceCodes;
    };

    handlePointNamesChange = (pointNames: string[]) => {
        this.props.store.filter.pointNames = pointNames;
    };

    private handleToleranceChange = e => (this.props.store.filter.onlyOutOfTolerance = e.currentTarget.checked);
}
